
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TourismPackage } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface PackageEditDialogProps {
  package?: TourismPackage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const PackageEditDialog = ({ package: tourismPackage, open, onOpenChange, onSuccess }: PackageEditDialogProps) => {
  const [formData, setFormData] = useState({
    title: '',
    destination: '',
    duration: '',
    price: 0,
    status: 'draft' as 'active' | 'draft' | 'expired',
    payment_status: 'unpaid' as 'paid' | 'pending' | 'unpaid',
    type: 'employee' as 'employee' | 'b2b',
    client_name: '',
    imageUrl: ''
  });

  // تحميل البيانات عند فتح النافذة أو تغيير الباقة
  useEffect(() => {
    console.log('Dialog opened:', open);
    console.log('Package data:', tourismPackage);
    
    if (open && tourismPackage) {
      // استخراج البيانات من notes field إذا كان موجوداً
      const notesParts = tourismPackage.title ? tourismPackage.title.split(' - ') : [];
      const title = notesParts[0] || tourismPackage.title || '';
      const destination = notesParts[1] || tourismPackage.destination || '';
      const duration = notesParts[2] || tourismPackage.duration || '';

      const newFormData = {
        title: title,
        destination: destination,
        duration: duration,
        price: tourismPackage.price || 0,
        status: tourismPackage.status || 'draft',
        payment_status: tourismPackage.payment_status || 'unpaid',
        type: tourismPackage.type || 'employee',
        client_name: tourismPackage.client_name || '',
        imageUrl: (tourismPackage.images && tourismPackage.images[0]) || ''
      };
      
      console.log('Setting form data:', newFormData);
      setFormData(newFormData);
    } else if (open && !tourismPackage) {
      // إعادة تعيين النموذج للباقات الجديدة
      setFormData({
        title: '',
        destination: '',
        duration: '',
        price: 0,
        status: 'draft',
        payment_status: 'unpaid',
        type: 'employee',
        client_name: '',
        imageUrl: ''
      });
    }
  }, [open, tourismPackage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tourismPackage) {
      toast.error("لا يمكن العثور على الباقة للتحديث");
      return;
    }

    try {
      console.log('Submitting form data:', formData);
      
      // تحديث البيانات في package_bookings
      const { error } = await supabase
        .from('package_bookings')
        .update({
          notes: `${formData.title} - ${formData.destination} - ${formData.duration}`,
          price: formData.price,
          status: formData.status === 'active' ? 'confirmed' : formData.status === 'draft' ? 'pending' : 'cancelled',
          payment_status: formData.payment_status === 'paid' ? 'paid' : 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', tourismPackage.id);

      if (error) throw error;

      toast.success("تم تحديث الباقة بنجاح");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating package:', error);
      toast.error("حدث خطأ أثناء تحديث الباقة");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل الباقة السياحية</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">عنوان الباقة</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="عنوان الباقة السياحية"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="destination">الوجهة</Label>
              <Input
                id="destination"
                value={formData.destination}
                onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                placeholder="الوجهة السياحية"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="duration">المدة</Label>
              <Input
                id="duration"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="مثال: 7 أيام"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">السعر</Label>
            <Input
              id="price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: Number(e.target.value) }))}
              placeholder="السعر بالريال السعودي"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">حالة الباقة</Label>
              <Select value={formData.status} onValueChange={(value: 'active' | 'draft' | 'expired') => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="active">نشطة</SelectItem>
                  <SelectItem value="expired">منتهية</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_status">حالة الدفع</Label>
              <Select value={formData.payment_status} onValueChange={(value: 'paid' | 'pending' | 'unpaid') => setFormData(prev => ({ ...prev, payment_status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">غير مدفوع</SelectItem>
                  <SelectItem value="paid">مدفوع</SelectItem>
                  <SelectItem value="pending">قيد الانتظار</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_name">اسم العميل</Label>
            <Input
              id="client_name"
              value={formData.client_name}
              onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
              placeholder="اسم العميل"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              حفظ التغييرات
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
